'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';

export const GTM_ID = 'GTM-N92TBJTP';

// Define global types
declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

// Check if analytics is allowed based on user consent
const isAnalyticsAllowed = () => {
  if (typeof window === 'undefined') return false;
  const consent = localStorage.getItem('analytics-consent');
  return consent === 'true';
};

// Initialize dataLayer with consent settings
const initializeDataLayer = () => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    // Set initial consent state
    const hasConsent = isAnalyticsAllowed();
    window.dataLayer.push({
      event: 'consent_update',
      analytics_consent: hasConsent ? 'granted' : 'denied',
      ad_consent: hasConsent ? 'granted' : 'denied'
    });
  }
};

// Simple event tracking (GTM handles the rest)
export const gtmPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && window.dataLayer && isAnalyticsAllowed()) {
    window.dataLayer.push({
      event: 'page_view',
      page_path: url,
      page_title: title || document.title,
      page_location: window.location.href
    });
  }
};

export const gtmCustomEvent = (eventName: string, parameters: Record<string, unknown> = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer && isAnalyticsAllowed()) {
    window.dataLayer.push({
      event: eventName,
      ...parameters
    });
  }
};

// Function to update consent (call this when user gives/revokes consent)
export const updateGTMConsent = (granted: boolean) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_update',
      analytics_consent: granted ? 'granted' : 'denied',
      ad_consent: granted ? 'granted' : 'denied'
    });
  }
};

export default function GoogleTagManager() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    initializeDataLayer();
    
    // Listen for storage events (consent changes)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'analytics-consent') {
        const hasConsent = e.newValue === 'true';
        updateGTMConsent(hasConsent);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <>
      {/* Initialize dataLayer before GTM loads */}
      <Script
        id="gtm-datalayer-init"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'gtm.start': new Date().getTime(),
              event: 'gtm.js'
            });
          `,
        }}
      />
      
      {/* Google Tag Manager Script - Always load for tag coverage */}
      <Script
        id="gtm-script"
        src={`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`}
        strategy="afterInteractive"
      />
    </>
  );
}

// GTM NoScript Fallback Component (for body) - Always render for coverage
export function GTMNoScript() {
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      />
    </noscript>
  );
} 